import type { MedplumClient } from "@medplum/core";
import type { Observation } from "@medplum/fhirtypes";

import * as Sentry from "@sentry/react";

import { observationHasSharedPostpartumRelatedCode } from "@/encounters/components/EncounterLabor/constants/shared-postpartum-related-observation-codes";
import { MEDPLUM_OBSERVATION_SEARCH_MAX_COUNT } from "@/encounters/constants/medplum-search";
import { joinReferenceString } from "@/utils/medplum";
import { toast } from "@/utils/notifications";
import { collectResourcesFromPaginatedBundle } from "@/utils/pagination";

interface FetchRelatedPatientPostpartumObservationsParams {
  medplum: MedplumClient;
  encounterId: string;
  relatedPatientId: string;
  isOptimistic: boolean | undefined;
}

interface FetchCanonicalNewbornPostpartumObservationsParams {
  medplum: MedplumClient;
  encounterId: string;
  newbornPatientId: string;
  isOptimistic: boolean | undefined;
}

/**
 * Reads every Observation charted on one Patient in an encounter, following each `next` page so a
 * busy delivery is never cut off at the first page.
 *
 * @param medplum - Medplum client
 * @param encounterId - The Labor encounter the Observations belong to
 * @param patientId - The Patient the Observations are charted on
 * @returns The Observations from every page, newest first
 */
export async function searchEncounterObservationsForPatient(
  medplum: MedplumClient,
  encounterId: string,
  patientId: string,
): Promise<Observation[]> {
  const firstPage = await medplum.search("Observation", {
    encounter: joinReferenceString("Encounter", encounterId),
    subject: joinReferenceString("Patient", patientId),
    _sort: "-_lastUpdated",
    _count: String(MEDPLUM_OBSERVATION_SEARCH_MAX_COUNT),
  });
  return collectResourcesFromPaginatedBundle<Observation>(medplum, firstPage, "Observation");
}

/**
 * Fetches the resolved newborn's own postpartum flowsheet observations for the Labor encounter's
 * "Newborn Postpartum Flow Sheet" (see `useNewbornFlowsheetSubject`). Unlike
 * {@link fetchRelatedPatientPostpartumObservations}, this never filters by
 * `observationHasSharedPostpartumRelatedCode` — every "newborn"-domain flowsheet entry is now
 * intentionally attributed to this Patient, not just the small set of codes that could ambiguously
 * belong to either subject.
 */
export async function fetchCanonicalNewbornPostpartumObservations({
  medplum,
  encounterId,
  newbornPatientId,
  isOptimistic,
}: FetchCanonicalNewbornPostpartumObservationsParams): Promise<Observation[]> {
  try {
    return await searchEncounterObservationsForPatient(medplum, encounterId, newbornPatientId);
  } catch (error) {
    console.warn("Failed to fetch canonical newborn postpartum observations");
    if (!isOptimistic) {
      Sentry.captureException(error);
      toast({
        type: "error",
        title: "Could not refresh newborn observations",
        message:
          "Newborn postpartum flowsheet updates may be delayed. Refresh the encounter and try again if the issue continues.",
        autoClose: false,
      });
    }
    return [];
  }
}

/**
 * Fetches related-patient (newborn) observations for labor encounter postpartum flowsheets.
 * Uses encounter + subject (no `code` filter) then filters to known flowsheet codes client-side.
 */
export async function fetchRelatedPatientPostpartumObservations({
  medplum,
  encounterId,
  relatedPatientId,
  isOptimistic,
}: FetchRelatedPatientPostpartumObservationsParams): Promise<Observation[]> {
  try {
    const allObservations = await searchEncounterObservationsForPatient(
      medplum,
      encounterId,
      relatedPatientId,
    );
    return allObservations.filter((observation) =>
      observationHasSharedPostpartumRelatedCode(observation),
    );
  } catch (error) {
    console.warn("Failed to fetch related-patient postpartum observations (newborn flowsheet)");
    if (!isOptimistic) {
      Sentry.captureException(error);
      toast({
        type: "error",
        title: "Could not refresh related newborn observations",
        message:
          "Newborn postpartum flowsheet updates may be delayed. Refresh the encounter and try again if the issue continues.",
        autoClose: false,
      });
    }
    return [];
  }
}
